import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  analyzeReport,
  fetchAnalyzeReportStatus,
  fetchDataFromS3,
  getDocumentPropertyNameFromSetting,
  loadAnalyzeReportExecutions,
  loadPreviousAnalysis,
  setActiveJurisdiction,
  setAnalyzeReportResults,
  setAnalyzeReportStatusLoadingState,
  setDocumentSettings,
  setExecutionId,
  setLoadPreviousAnalysisStatus,
  setSuggestionStatus,
  setSuggestionsToWordDocument,
  SuggestionsState,
} from '../redux/suggestions';
import { ItpExecutionStatuses } from '@xbs/xbs-enums';
import { FetchLoadingStateEnum } from '../constants';
import { wordSetting } from '../models/wordStorageData';
import { ExperimentState } from '../redux/experiments';
import { MessageReceivedEvent } from '../models/webSockets';
import SocketService from '../services/socket/service';
import { setDocumentSetting } from '../components/exactera/api';

export const listenerMiddleware = createListenerMiddleware();

const POLLING_INTERVAL = 3000;

listenerMiddleware.startListening({
  actionCreator: setSuggestionStatus.fulfilled,
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState() as { suggestions: SuggestionsState };
    const itpExecutionId = state.suggestions.executionId;

    // save the suggestions to document state
    await listenerApi.dispatch(setSuggestionsToWordDocument({ wordSettings: [{ settingName: 'suggestions', settingValue: state.suggestions.suggestions }] }));
    const socketService = SocketService.getInstance();
    socketService.broadcastMessage({
      event: MessageReceivedEvent.RECOMMENDATION_STATUS_CHANGE,
      data: {
        itpExecutionId,
      },
    });
  },
});

listenerMiddleware.startListening({
  actionCreator: analyzeReport.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as { suggestions: SuggestionsState };
    const useMockResponse = state.suggestions.flags.useMockResponse;
    const { itpExecutionId } = action.meta.arg as { itpExecutionId: string; };
    listenerApi.dispatch(fetchAnalyzeReportStatus({ itpExecutionId, useMockResponse }));
  },
});

listenerMiddleware.startListening({
  actionCreator: fetchAnalyzeReportStatus.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as { suggestions: SuggestionsState };
    const useMockResponse = state.suggestions.flags.useMockResponse;
    const { itpExecutionId } = action.meta.arg as { itpExecutionId: string; } || { itpExecutionId: 'test-execution' };
    const status: string = action.payload.status;
    const socketService = SocketService.getInstance();

    switch (status) {
      case ItpExecutionStatuses.ByName.Pending.Name:
        await listenerApi.delay(POLLING_INTERVAL);
        await listenerApi.dispatch(
          fetchAnalyzeReportStatus({ itpExecutionId, useMockResponse })
        );
        break;

      case ItpExecutionStatuses.ByName.Success.Name:
        await listenerApi.dispatch(
          loadAnalyzeReportExecutions({ itpExecutionId, useMockResponse })
        );
        break;

      case ItpExecutionStatuses.ByName.Failure.Name:
        await listenerApi.dispatch(
          setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.error)
        );
        // send 'error' message to other users
        socketService.broadcastMessage({
          event: MessageReceivedEvent.ANALYSIS_ERROR,
          data: {
            itpExecutionId,
          },
        });
        break;

      default:
        break;
    }
  },
});

listenerMiddleware.startListening({
  actionCreator: loadAnalyzeReportExecutions.fulfilled,
  effect: (action, listenerApi) => {
    const signedUrl = action.payload.body;
    const state = listenerApi.getState() as { suggestions: SuggestionsState };
    const useMockResponse = state.suggestions.flags.useMockResponse;
    listenerApi.dispatch(fetchDataFromS3({ signedUrl, useMockResponse }));
  },
});

listenerMiddleware.startListening({
  actionCreator: fetchDataFromS3.fulfilled,
  effect: (action, listenerApi) => {
    const s3DataResult = action.payload;
    listenerApi.dispatch(setAnalyzeReportResults(s3DataResult));
    listenerApi.dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.fulfilled));
    // save suggestions to Word document
    const state = listenerApi.getState() as { suggestions: SuggestionsState, experiments: ExperimentState };
    const analysisIsExperiment = state.experiments.hasRunExperiment || state.experiments.isRunning;
    const itpExecutionId = state.suggestions.executionId;
    if (!analysisIsExperiment) {
      const settingsToSet: wordSetting[] = [
        { settingName: 'itpExecutionId', settingValue: itpExecutionId },
        { settingName: 'backgroundData', settingValue: s3DataResult.backgroundData },
        { settingName: 'suggestions', settingValue: s3DataResult.suggestions },
      ];
      listenerApi.dispatch(setSuggestionsToWordDocument({ wordSettings: settingsToSet }));
    }
    // send 'fulfilled' message to other users
    const socketService = SocketService.getInstance();
    socketService.broadcastMessage({
      event: MessageReceivedEvent.ANALYSIS_END,
      data: {
        itpExecutionId,
      },
    });
  },
});


listenerMiddleware.startListening({
  actionCreator: loadPreviousAnalysis.fulfilled,
  effect: (action, listenerApi) => {
    const { suggestions, backgroundData, itpExecutionId } = action.payload;
    listenerApi.dispatch(setExecutionId(itpExecutionId));
    listenerApi.dispatch(setAnalyzeReportResults({ suggestions, backgroundData }));
    listenerApi.dispatch(setActiveJurisdiction(backgroundData?.jurisdiction || null));
    listenerApi.dispatch(setLoadPreviousAnalysisStatus(FetchLoadingStateEnum.fulfilled));
  },
});

listenerMiddleware.startListening({
  matcher: isAnyOf(
    fetchDataFromS3.rejected,
    fetchAnalyzeReportStatus.rejected,
    loadAnalyzeReportExecutions.rejected,
    analyzeReport.rejected
  ),
  effect: (action, listenerApi) => {
    setDocumentSetting('isLoading', false);
    listenerApi.dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.error));
    console.error("Error in action: ", action);

    const state = listenerApi.getState() as { suggestions: SuggestionsState, experiments: ExperimentState };
    const itpExecutionId = state.suggestions.executionId;
    const socketService = SocketService.getInstance();
    socketService.broadcastMessage({
      event: MessageReceivedEvent.ANALYSIS_ERROR,
      data: {
        itpExecutionId,
      },
    });
  },
});

listenerMiddleware.startListening({
  actionCreator: getDocumentPropertyNameFromSetting.fulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(setDocumentSettings(action.payload));
  }
});
